import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import uniqueId from 'lodash-es/uniqueId'

import { NOTIFICATION_TYPE } from '../../constants/common'
import { Notifications } from '../../context'

import { parseNotificationProps } from './useNotification.utils'

/**
 * handleAddInfoNotification,
 * handleAddErrorNotification,
 * handleAddSuccessNotification,
 * handleAddWarningNotification,
 *
 * methods bellow accept either translation key or object as a parameter for push notification
 *
 * @example 1
 * handleAddSuccessNotification('Notification.NotificationKey')
 *
 * @example 2
 * handleAddSuccessNotification({
 *   title:  t('Notification.NotificationKey')
 * })
 *
 * handleAddNotification - accept object of notification params
 *
 * @example
 * handleAddNotification({
 *   type: NOTIFICATION_TYPE.success,
 *   title:  t('Notification.NotificationKey')
 * })
 *
 * handleRemoveNotification - accept notification id for remove
 */

export function useNotification() {
  const { t } = useTranslation()

  const { addNotification, queue, removeNotification } =
    useContext(Notifications)

  function handleAddNotification(notification) {
    addNotification({
      ...notification,
      id: notification.id || `notification-${uniqueId()}`,
    })
  }

  function handleRemoveNotification(id) {
    removeNotification(id)
  }

  /**
   * accept either translation key or object as a parameter for push notification
   */
  function handleAddSuccessNotification(notification) {
    handleAddNotification({
      ...parseNotificationProps(notification, t),
      type: NOTIFICATION_TYPE.success,
    })
  }

  /**
   * accept either translation key or object as a parameter for push notification
   */
  function handleAddErrorNotification(notification) {
    handleAddNotification({
      ...parseNotificationProps(notification, t),
      type: NOTIFICATION_TYPE.error,
    })
  }

  /**
   * accept either translation key or object as a parameter for push notification
   */
  function handleAddWarningNotification(notification) {
    handleAddNotification({
      ...parseNotificationProps(notification, t),
      type: NOTIFICATION_TYPE.warning,
    })
  }

  /**
   * accept either translation key or object as a parameter for push notification
   */
  function handleAddInfoNotification(notification) {
    handleAddNotification({
      ...parseNotificationProps(notification, t),
      type: NOTIFICATION_TYPE.info,
    })
  }

  return {
    handleAddErrorNotification,
    handleAddInfoNotification,
    handleAddNotification,
    handleAddSuccessNotification,
    handleAddWarningNotification,
    handleRemoveNotification,
    queue,
  }
}
